import React, { useEffect, useMemo, useState } from "react";
import "./StripeUpdateCardModal.scss";
import ReactModal from "react-modal";
import { loadStripe, Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Socket } from "socket.io-client";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { LoadingResults } from "../../shared/LoadingResults/LoadingResults";
import { SubscriptionPaymentForm } from "../../onboarding/OnboardingBilling/OnboardingBilling";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IStripeCreateSubscriptionSetupIntentDTO from "../../../models/DTO/IStripeCreateSubscriptionSetupIntentDTO";
import IStripeOnCreateSubscriptionSetupIntentDTO from "../../../models/DTO/IStripeOnCreateSubscriptionSetupIntentDTO";
import IStripeCompleteSubscriptionSetupDTO from "../../../models/DTO/IStripeCompleteSubscriptionSetupDTO";
import IStripeOnCompleteSubscriptionSetupDTO from "../../../models/DTO/IStripeOnCompleteSubscriptionSetupDTO";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import UIService from "../../../services/UIService";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    closeModalFn: () => void;
    /** The new method is on file (or pending bank verification); the Billing page refreshes from this. */
    onUpdatedFn: (data: IStripeOnCompleteSubscriptionSetupDTO) => void;
};

/**
 * Billing page: replace the card / bank account the Orderwerks subscription is charged to. Same mechanics as the
 * onboarding billing step: the API issues a SetupIntent, Stripe's Payment Element collects card or US bank
 * account details, and on confirmation the API makes it the customer's default and points the subscription at it.
 */
export const StripeUpdateCardModal: React.FC<Props> = (props) => {
    const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
        return process.env.STRIPE_KEY ? loadStripe(process.env.STRIPE_KEY) : null;
    }, []);

    const [clientSecret, setClientSecret] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [verificationUrl, setVerificationUrl] = useState<string | null>(null);

    const basePayload = () => ({
        userGuid: props.userModel.guid,
        userToken: props.userModel.token,
        supplierGuid: props.supplierModel.guid,
    });

    const createSetupIntent = () => {
        setHasError(false);
        setClientSecret(undefined);

        const payload: IStripeCreateSubscriptionSetupIntentDTO = basePayload();

        props.socket.emit(EndpointsEnum.StripeCreateSubscriptionSetupIntent, payload);
    };

    const completeSetup = (setupIntentId: string) => {
        setSaving(true);

        const payload: IStripeCompleteSubscriptionSetupDTO = { ...basePayload(), setupIntentId };

        props.socket.emit(EndpointsEnum.StripeCompleteSubscriptionSetup, payload);
    };

    const onCreateSetupIntent = (data: IStripeOnCreateSubscriptionSetupIntentDTO) => {
        setClientSecret(data.client_secret);
    };

    const onCompleteSetup = (data: IStripeOnCompleteSubscriptionSetupDTO) => {
        setSaving(false);

        const pending = data.status === "requires_action";

        const notification: INotification = {
            message: pending ? "bank_account_verification_pending" : "payment_method_saved",
            level: pending ? NotificationLevelEnum.warning : NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        props.notifyFn(null, notification);
        props.onUpdatedFn(data);

        if (pending && data.verificationUrl) {
            // Keep the modal up so they can get to Stripe's micro-deposit verification page.
            setVerificationUrl(data.verificationUrl);
        } else {
            props.closeModalFn();
        }
    };

    const notify = (data: IErrorDTO) => {
        if (
            data?.message === "ERROR_STRIPE_CREATE_SUBSCRIPTION_SETUP_INTENT" ||
            data?.message === "ERROR_STRIPE_COMPLETE_SUBSCRIPTION_SETUP"
        ) {
            setSaving(false);
            setHasError(true);
        }
    };

    useEffect(() => {
        props.socket.on(EndpointsEnum.StripeOnCreateSubscriptionSetupIntent, onCreateSetupIntent);
        props.socket.on(EndpointsEnum.StripeOnCompleteSubscriptionSetup, onCompleteSetup);
        props.socket.on(EndpointsEnum.Notify, notify);

        createSetupIntent();

        return () => {
            props.socket.removeListener(EndpointsEnum.StripeOnCreateSubscriptionSetupIntent, onCreateSetupIntent);
            props.socket.removeListener(EndpointsEnum.StripeOnCompleteSubscriptionSetup, onCompleteSetup);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
        };
    }, []);

    const elementsOptions: StripeElementsOptions | undefined = clientSecret
        ? {
              clientSecret,
              appearance: { theme: UIService.isMainViewLight() ? "stripe" : "night" },
          }
        : undefined;

    const modalStyle = {
        overlay: {
            zIndex: 999999999,
        },
        content: {
            top: "10%",
            maxWidth: "34rem",
            margin: "0 auto",
            bottom: "auto",
            height: "auto",
            maxHeight: "80vh",
            overflow: "auto",
        },
    };

    return (
        <ReactModal
            isOpen={true}
            contentLabel="Update Payment Method"
            style={modalStyle}
            className={"modalClass"}
            overlayClassName={"overlayClass"}
            onRequestClose={props.closeModalFn}
        >
            <div className={"StripeUpdateCardModal p-2"}>
                <div className={"w-100"}>
                    <div className={"d-flex justify-content-between"}>
                        <OwTitle leftIconName={"fad fa-credit-card"} title={"update_card"} tag={"h3"} />
                        <div>
                            <OwButton
                                className={"btn-danger"}
                                centerIconClass={"fas fa-times"}
                                fn={props.closeModalFn}
                                testId={"update-card-close"}
                            />
                        </div>
                    </div>
                </div>

                <div className={"mt-3"}>
                    {verificationUrl ? (
                        <div data-testid={"update-card-verification"}>
                            <div className={"text-warning"}>
                                <OwLabel title={"bank_account_verification_pending"} />
                            </div>
                            <a
                                href={verificationUrl}
                                target={"_blank"}
                                rel={"noreferrer"}
                                className={"btn btn-sm btn-outline-primary mt-2"}
                            >
                                <OwLabel title={"verify_bank_account"} />
                            </a>
                            <div className={"mt-3"}>
                                <OwButton title={"done"} fn={props.closeModalFn} />
                            </div>
                        </div>
                    ) : hasError ? (
                        <div className={"text-danger"} role={"alert"}>
                            <OwLabel title={"error_saving_payment_method"} />
                            <div className={"mt-2"}>
                                <OwButton className={"btn-secondary"} title={"retry"} fn={createSetupIntent} />
                            </div>
                        </div>
                    ) : stripePromise && elementsOptions ? (
                        <Elements key={clientSecret} stripe={stripePromise} options={elementsOptions}>
                            <SubscriptionPaymentForm
                                returnUrl={`${window.location.origin}/billing`}
                                saving={saving}
                                setSaving={setSaving}
                                onConfirmed={completeSetup}
                                onCancel={props.closeModalFn}
                                cancelTitle={"cancel"}
                            />
                        </Elements>
                    ) : (
                        <LoadingResults />
                    )}
                </div>
            </div>
        </ReactModal>
    );
};
